import { useCallback, useEffect, useState } from "react";
import type { RequestResult } from "@touchscreen/api-client";
import { requestErrorMessage, useRequestTrack, useTouchscreenContext } from "@touchscreen/hooks";
import { getFreshPosition } from "./geolocation.js";

export type RequestFlowState =
  | { status: "idle" }
  /** Running the pre-check `venuesNearby` gate before the confirm dialog can show a real Request
   * button — happens the moment the flow is mounted, not on tapping Request. */
  | { status: "checking_geofence" }
  /** The pre-check (or the final fresh position at submit time) failed — permission denied,
   * unsupported, or granted but out of range. Either way presence hasn't been proven, so the
   * caller swaps the Request button for a "Share location" one back to the venue picker rather
   * than surfacing an error to retry against. */
  | { status: "share_location" }
  | { status: "locating" }
  | { status: "loading" }
  | { status: "success"; result: RequestResult }
  | { status: "duplicate"; result: RequestResult }
  | { status: "error"; message: string };

/**
 * Wraps `useRequestTrack` with the phone's geofence handling: when the venue requires it, a
 * fresh position is fetched right before submit — never cached, per touchscreen-api.md. Kept
 * local to the phone app rather than in `@touchscreen/hooks` since geolocation is a phone-only
 * concern (the kiosk is bolted to a wall).
 *
 * Until presence has been proven this session (`geofencePassed`), mounting this hook runs a
 * `venuesNearby` pre-check up front — before the confirm dialog has a chance to render a Request
 * button — so the caller can show a "Share location" button instead of a Request button that
 * would just fail. This is a lightweight client-side gate, not the authoritative one — the
 * actual `POST /requests` below still sends a *second*, brand-new fresh position and can still be
 * rejected server-side (e.g. someone who passed the check then walked off). Once passed, it's
 * cached on the shared touchscreen context for the rest of this venue mount, so every later
 * mount/submit skips straight to the real request.
 *
 * Deliberately doesn't pre-fetch or surface "requests remaining this hour" — that's not
 * something to alert on ahead of time. `requester_limit`/`venue_limit` only become a message
 * here (via `requestErrorMessage`) once a submit actually hits the wall.
 */
export function useRequestFlow(): {
  flow: RequestFlowState;
  submit: (smId: string) => Promise<void>;
  reset: () => void;
  isCoolingDown: (smId: string) => boolean;
} {
  const { client, venue, geofencePassed, setGeofencePassed } = useTouchscreenContext();
  const { state: trackState, requestTrack, isCoolingDown, reset: resetTrack } = useRequestTrack();
  const [phase, setPhase] = useState<"idle" | "checking_geofence" | "share_location" | "locating">("idle");

  const geofenceRequired = venue?.geofence.required ?? false;

  useEffect(() => {
    if (!geofenceRequired || geofencePassed || !venue) return;
    let cancelled = false;
    setPhase("checking_geofence");
    void (async () => {
      const check = await getFreshPosition();
      if (cancelled) return;
      if (check.status !== "granted") {
        setPhase("share_location");
        return;
      }
      const nearby = await client.venuesNearby(check.lat, check.lng);
      if (cancelled) return;
      if (!nearby.some((v) => v.ref === venue.ref)) {
        setPhase("share_location");
        return;
      }
      setGeofencePassed(true);
      setPhase("idle");
    })();
    return () => {
      cancelled = true;
    };
  }, [geofenceRequired, geofencePassed, venue, client, setGeofencePassed]);

  const submit = useCallback(
    async (smId: string) => {
      let position: { lat: number; lng: number } | undefined;
      if (geofenceRequired) {
        setPhase("locating");
        const outcome = await getFreshPosition();
        if (outcome.status !== "granted") {
          setPhase("share_location");
          return;
        }
        position = { lat: outcome.lat, lng: outcome.lng };
      }

      setPhase("idle");
      await requestTrack(smId, position ? { position } : undefined);
    },
    [geofenceRequired, requestTrack],
  );

  const reset = useCallback(() => {
    resetTrack();
    setPhase("idle");
  }, [resetTrack]);

  const flow: RequestFlowState =
    phase === "checking_geofence"
      ? { status: "checking_geofence" }
      : phase === "share_location"
        ? { status: "share_location" }
        : phase === "locating"
          ? { status: "locating" }
          : trackState.status === "loading"
            ? { status: "loading" }
            : trackState.status === "error"
              ? { status: "error", message: requestErrorMessage(trackState.error) }
              : trackState.status === "success"
                ? { status: trackState.result.duplicate ? "duplicate" : "success", result: trackState.result }
                : { status: "idle" };

  return { flow, submit, reset, isCoolingDown };
}
